import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Mail, Phone, Calendar, FileText, Palette, Users, Image as ImageIcon } from "lucide-react";
import { type ArtistDetail } from "@/hooks/useArtistDetails";

interface ViewArtistDetailProps {
  artistDetail: ArtistDetail;
  onClose: () => void;
  onEdit: () => void;
}

const getStatusColor = (status: ArtistDetail['status']) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'reviewed':
      return 'bg-blue-100 text-blue-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function ViewArtistDetail({
  artistDetail,
  onClose,
  onEdit,
}: ViewArtistDetailProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{artistDetail.full_name}</h2>
          <div className="flex items-center gap-4 mt-1">
            <p className="text-gray-600">{artistDetail.email_address}</p>
            <span className="font-mono text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
              ID: {artistDetail.artistid}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onEdit}>
            Edit Details
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-sm">{artistDetail.full_name}</p>
              </div>
                          <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a
                  href={`mailto:${artistDetail.email_address}`}
                  className="text-blue-600 hover:underline text-sm"
                >
                  {artistDetail.email_address}
                </a>
              </div>
            </div>
            </div>
            {artistDetail.phone_number && (
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a
                    href={`tel:${artistDetail.phone_number}`}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    {artistDetail.phone_number}
                  </a>
                </div>
              </div>
            )}
            {artistDetail.whatsapp_number && (
              <div>
                <label className="text-sm font-medium text-gray-500">WhatsApp</label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span className="text-sm">{artistDetail.whatsapp_number}</span>
                </div>
              </div>
            )}
            {artistDetail.full_address && (
              <div>
                <label className="text-sm font-medium text-gray-500">Full Address</label>
                <p className="text-sm">{artistDetail.full_address}</p>
              </div>
            )}
            {artistDetail.city_state && (
              <div>
                <label className="text-sm font-medium text-gray-500">City/State</label>
                <p className="text-sm">{artistDetail.city_state}</p>
              </div>
            )}
            {artistDetail.pin_code && (
              <div>
                <label className="text-sm font-medium text-gray-500">Pin Code</label>
                <p className="text-sm">{artistDetail.pin_code}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Banking & Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Banking & Availability
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {artistDetail.bank_account_details && (
              <div>
                <label className="text-sm font-medium text-gray-500">Bank Account Details</label>
                <p className="text-sm whitespace-pre-line">{artistDetail.bank_account_details}</p>
              </div>
            )}
            {artistDetail.upi_id && (
              <div>
                <label className="text-sm font-medium text-gray-500">UPI ID</label>
                <p className="text-sm">{artistDetail.upi_id}</p>
              </div>
            )}
            {artistDetail.availability && (
              <div>
                <label className="text-sm font-medium text-gray-500">Availability</label>
                <p className="text-sm">{artistDetail.availability}</p>
              </div>
            )}
          </CardContent>
        </Card>



        {/* Timeline & Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Timeline & Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                <Badge className={getStatusColor(artistDetail.status)}>
                  {artistDetail.status}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Application Date</label>
              <p className="text-sm">
                {new Date(artistDetail.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-sm">
                {new Date(artistDetail.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
